/**
 * Turn a failed response into something a person can act on.
 *
 * Callers used to do `const d = await res.json().catch(() => ({}));
 * alert(d.error || 'Failed to save')`, which collapses every failure that is
 * not our own JSON into one unhelpful sentence. The case that matters here:
 * a web application firewall sits in front of the API and rejects request
 * bodies that look like an attack payload, answering with an HTML 403 that
 * never reaches the application. On a penetration testing platform that is a
 * routine thing to type -- a recommendation explaining output encoding
 * contains the very string the firewall objects to -- and the generic message
 * left the author bisecting their own prose to find out which paragraph the
 * product disliked.
 */
export async function apiErrorMessage(res, fallback = 'Request failed') {
  const text = await res.text().catch(() => '');

  // Our own errors are JSON and already say what went wrong.
  try {
    const d = JSON.parse(text);
    if (d && d.error) return d.error;
  } catch { /* not our JSON — fall through to the shapes below */ }

  const looksLikeFirewall = res.status === 403 && /<html|403 Forbidden/i.test(text);
  if (looksLikeFirewall) {
    return 'Blocked before it reached the server.\n\n'
      + 'A web application firewall in front of this site rejects requests whose '
      + 'content resembles an attack payload. Text describing one counts: script '
      + 'tags, SQL fragments like \' OR \'1\'=\'1, and paths like ../../etc/passwd '
      + 'are all refused, even when written as documentation and even when already '
      + 'HTML-encoded.\n\n'
      + 'Nothing was saved. Removing or rewording that passage will let it through; '
      + 'the firewall rule needs relaxing for this endpoint to fix it properly.';
  }

  if (res.status === 413) {
    return 'That content is too large for the server to accept. Nothing was saved.';
  }
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return `The server did not respond (HTTP ${res.status}). Nothing was saved — try again shortly.`;
  }

  return `${fallback} (HTTP ${res.status})`;
}
